import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { MODES } from "@/modes";
import { RoomSettingsForm } from "@/components/game/SettingFields";
import { EMPTY_TAGS, saveTags, TagPicker } from "@/components/teacher/Social";
import { SettingTemplates } from "@/components/teacher/SettingTemplates";
import { InfoTip, SettingSelect } from "@/components/game/SettingFields";
import { Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ConsentCheck, ROOM_CONSENT } from "@/components/Legal";
import { useCatalog } from "@/lib/catalog";

const GENERIC = { team_count: 3, team_selection: "student", seats_per_team: 8, board_names: "initial", sound: true };

export const CreateGameDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [modes, setModes] = useState([]);
  const [pools, setPools] = useState([]);
  const [mode, setMode] = useState("quiz");
  const [poolIds, setPoolIds] = useState([]);
  const [settings, setSettings] = useState(GENERIC);
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState(EMPTY_TAGS);
  const [classes, setClasses] = useState([]);
  const [consent, setConsent] = useState(false);
  const catalog = useCatalog();
  const { teacher } = useAuth();
  const dev = Boolean(teacher?.developer);

  useEffect(() => {
    if (!open) return;
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    Promise.all([api.get("/modes"), api.get("/pools")]).then(([m, p]) => {
      setModes(m.data.filter((x) => MODES[x.key]));
      setPools(p.data);
      const first = m.data.find((x) => dev || !x.locked);
      if (first) { setMode(first.key); setSettings({ ...GENERIC, ...first.default_settings }); }
    }).catch((e) => toast.error(errMsg(e)));
  }, [open, dev]);

  const toggle = (id) => setPoolIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { mode, pool_ids: poolIds, settings });
      await saveTags("rooms", data.id, tags);
      navigate(`/ogretmen/oyun/${data.code}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/preview", { mode, pool_ids: poolIds, settings });
      window.open(`/onizleme/${data.code}`, "_blank");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const ModeForm = MODES[mode]?.SettingsForm;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[rgba(15,23,42,0.55)] border-[rgba(71,85,105,0.45)] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-game-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">Yeni oyun</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <section className="space-y-2">
            <p className="eyebrow text-[#FACC15]">Mod</p>
            <div className="flex flex-wrap gap-2">
              {modes.map((m) => {
                const off = m.locked && !dev;
                return (
                <button key={m.key} disabled={off} onClick={() => { setMode(m.key); setSettings((s) => ({ ...s, ...m.default_settings })); }} data-testid={`mode-${m.key}`}
                  className={`px-4 py-3 rounded-lg border text-left flex items-center gap-2 ${off ? "opacity-40 cursor-not-allowed" : ""} ${mode === m.key ? "border-[#FACC15] bg-[#FACC15]/10" : "border-[rgba(71,85,105,0.45)]"}`}>
                  {m.locked && <Lock className="h-4 w-4 text-slate-300" data-testid={`mode-locked-${m.key}`} />}
                  <span className="font-bold">{m.label}</span><InfoTip text={m.description} testId={`mode-info-${m.key}`} />
                </button>
                );
              })}
              {(catalog.upcoming || []).map((u) => (
                <div key={u.key} className="px-4 py-3 rounded-lg border border-dashed border-[rgba(71,85,105,0.6)] flex items-center gap-2 opacity-60 cursor-not-allowed" data-testid={`upcoming-mode-${u.key}`}>
                  <span className="font-bold">{u.label}</span>
                  <span className="text-[10px] font-bold uppercase rounded-full bg-[#FACC15]/20 text-[#FACC15] px-2 py-0.5">Yakında</span>
                  <InfoTip text={u.description} testId={`upcoming-info-${u.key}`} />
                </div>
              ))}
            </div>
          </section>
          <section className="space-y-2">
            <p className="eyebrow text-[#FACC15]">Soru havuzları (birden fazla seçilirse birleştirilir)</p>
            {pools.map((p) => (
              <label key={p.id} className="flex items-center gap-3 rounded-lg border border-[rgba(71,85,105,0.45)] px-3 py-2.5 cursor-pointer">
                <Checkbox checked={poolIds.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`pool-check-${p.id}`} />
                <span className="flex-1 font-semibold">{p.name}</span>
                <span className="text-sm text-slate-400">{p.question_count} soru</span>
              </label>
            ))}
          </section>
          <section className="space-y-2">
            <TagPicker value={tags} onChange={setTags} />
          </section>
          <section className="space-y-2">
            <p className="eyebrow text-[#FACC15]">Ayarlar</p>
            {classes.length > 0 && <SettingSelect label="Sınıf (katılanlar otomatik eklenir)" value={settings.class_id || "none"} testId="game-class-select"
              onChange={(v) => setSettings((s) => ({ ...s, class_id: v === "none" ? null : v }))} options={[["none", "Sınıf seçilmedi"], ...classes.map((c) => [c.id, c.name])]} />}
            <SettingTemplates mode={mode} settings={settings} onApply={(s) => setSettings((x) => ({ ...x, ...s }))} />
            <RoomSettingsForm settings={settings} onChange={(patch) => setSettings((s) => ({ ...s, ...patch }))} ModeForm={ModeForm} />
          </section>
        </div>
        <ConsentCheck checked={consent} onChange={setConsent} testId="create-game-consent">{ROOM_CONSENT}</ConsentCheck>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={preview} disabled={busy || poolIds.length === 0} className="h-12" data-testid="create-game-preview">Önizle</Button>
          <Button onClick={create} disabled={busy || !consent || poolIds.length === 0 || (modes.find((m) => m.key === mode)?.locked && !dev)} className="font-black h-12 px-6" data-testid="create-game-submit">Oyunu oluştur</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
